// components/MetadataEditor.tsx
"use client";
import React, { useState, useEffect } from 'react';
import type { MetadataItem } from '@interfaces'

const MetadataEditor: React.FC<any> = ({
  data,
  apiEndpoint = "https://thedailydialectics.com/api/update-item",
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [items, setItems] = useState<MetadataItem[]>(data);
  const [formData, setFormData] = useState<MetadataItem>(
    data[0] || { url: '', title: '' }
  );

  useEffect(() => {
    setItems(data);
    setFormData(data[0] || { url: '', title: '' });
  }, [data]);

  useEffect(() => {
    if (items.length > 0) {
      setFormData({ ...items[currentIndex] });
    }
  }, [currentIndex, items]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: keyof MetadataItem
  ) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      [fieldName]: value.split(',').map(item => item.trim())
    }));
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      description: e.target.value
    }));
  };

  const updateItem = async () => {
    const updatedItems = [...items];
    updatedItems[currentIndex] = formData;
    setItems(updatedItems);

    try {
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ index: currentIndex, item: formData }),
      });
      if (!response.ok) throw new Error('Network response was not ok');
      console.log('Item updated successfully');
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const nextItem = () => {
    setCurrentIndex(prev => (prev + 1) % items.length);
  };

  const prevItem = () => {
    setCurrentIndex(prev => (prev - 1 + items.length) % items.length);
  };

  if (!items || items.length === 0) {
    return <div>No metadata items available to edit</div>;
  }

  // Normalize description for display
  const getDescriptionString = (desc: MetadataItem['description']) => {
    if (!desc) return '';
    if (Array.isArray(desc)) {
      return typeof desc[0] === 'string' ? desc[0] : desc.join(', ');
    }
    return desc;
  };

  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      margin: '20px 0',
      border: '1px solid #ccc',
      padding: '20px',
      borderRadius: '5px'
    }}>
      <div style={{ marginBottom: '15px' }}>
        <h2 style={{ margin: '0 0 10px 0' }}>
          {formData.title
            ? Array.isArray(formData.title)
              ? formData.title.join(' - ')
              : formData.title
            : 'No title provided'}
        </h2>
        <div>
          <button
            onClick={prevItem}
            style={{ marginRight: '10px', padding: '5px 10px' }}
            disabled={items.length <= 1}
          >
            Previous
          </button>
          <button
            onClick={nextItem}
            style={{ padding: '5px 10px' }}
            disabled={items.length <= 1}
          >
            Next
          </button>
          <span style={{ marginLeft: '15px' }}>
            Item {currentIndex + 1} of {items.length}
          </span>
        </div>
      </div>

      {formData.type === 'image' ? (
        <img
          src={formData.url}
          alt={formData.alt || 'Preview'}
          style={{
            maxWidth: '800px',
            maxHeight: '400px',
            marginBottom: '15px',
            border: '1px solid #ccc'
          }}
        />
      ) : (
        <iframe
          title="webpage-preview"
          src={formData.url}
          width="800"
          height="400"
          style={{ border: '1px solid #ccc', marginBottom: '15px' }}
        />
      )}

      <div style={{ display: 'grid', gap: '15px' }}>
        <label>
          URL:
          <input
            type="text"
            name="url"
            value={formData.url || ''}
            onChange={handleInputChange}
            style={{ width: '100%', padding: '5px', marginTop: '5px' }}
          />
        </label>

        <label>
          Title (comma separated):
          <input
            type="text"
            name="title"
            value={Array.isArray(formData.title) ? formData.title.join(', ') : formData.title || ''}
            onChange={(e) => handleArrayInputChange(e, 'title')}
            style={{ width: '100%', padding: '5px', marginTop: '5px' }}
          />
        </label>

        <label>
          Description:
          <textarea
            name="description"
            value={getDescriptionString(formData.description)}
            onChange={handleDescriptionChange}
            rows={4}
            style={{ width: '100%', padding: '5px', marginTop: '5px' }}
          />
        </label>

        <label>
          Keywords (comma separated):
          <input
            type="text"
            name="keywords"
            value={Array.isArray(formData.keywords) ? formData.keywords.join(', ') : formData.keywords || ''}
            onChange={(e) => handleArrayInputChange(e, 'keywords')}
            style={{ width: '100%', padding: '5px', marginTop: '5px' }}
          />
        </label>

        <label>
          Type:
          <input
            type="text"
            name="type"
            value={formData.type || ''}
            onChange={handleInputChange}
            style={{ width: '100%', padding: '5px', marginTop: '5px' }}
          />
        </label>

        <label>
          Extension:
          <input
            type="text"
            name="ext"
            value={formData.ext || ''}
            onChange={handleInputChange}
            style={{ width: '100%', padding: '5px', marginTop: '5px' }}
          />
        </label>

        <button
          onClick={updateItem}
          style={{
            padding: '8px 15px',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '10px'
          }}
        >
          Update Item
        </button>
      </div>
    </div>
  );
};

export default MetadataEditor;
